// API functions for compliance frameworks (obligations) and related sub-resources.
// Frameworks are organized by framework categories (permission-filtered server-side).
// Library endpoints are non-confidential platform data served via the same tenant path.

import type { ApiClient } from './client'

// --- Types ---

/** Framework library entry (platform-level, non-confidential). */
export interface LibraryEntry {
  id: string
  category: string
  name: string
  description?: string | null
  version_label: string
  copyright_notice?: string | null
  uploaded_at: string
}

/** Framework library detail (adds item_count). */
export interface LibraryEntryDetail extends LibraryEntry {
  item_count: number
}

/** Compliance framework list response. */
export interface ComplianceFramework {
  id: string
  name: string
  description?: string | null
  category_id: string
  category_name: string
  item_count: number
  mapped_count: number
  unmapped_count: number
  imported_from?: string | null
  imported_at?: string | null
  created_at: string
}

/** Coverage summary counts by level. */
export interface CoverageSummary {
  full: number
  partial: number
  planned: number
  not_assessed: number
  unmapped: number
}

/** Framework detail response (extends list with coverage and requirement groups). */
export interface ComplianceFrameworkDetail extends ComplianceFramework {
  coverage_summary: CoverageSummary
  requirement_groups: string[]
}

/** Framework item (requirement within a framework). */
export interface FrameworkItem {
  id: string
  framework_id: string
  reference_code: string
  title: string
  description?: string | null
  requirement_group: string
  sub_requirement: string
  tenant_interpretation?: string | null
  mapping_count: number
  is_mapped: boolean
}

/** Control-compliance mapping shown on item detail. */
export interface ItemMapping {
  id: string
  control_id: string
  control_name: string
  coverage_level: string
  coverage_notes?: string | null
  mapped_at: string
  mapped_by: string
  mapped_by_name: string
}

/** Framework item detail with mappings. */
export interface FrameworkItemDetail extends FrameworkItem {
  mappings: ItemMapping[]
}

/** Import result response. */
export interface ImportResult {
  framework_id: string
  item_count: number
  error_count: number
  errors: ImportError[]
}

/** Per-row import error. */
export interface ImportError {
  row_number: number
  message: string
}

/** Mapping creation response. */
export interface MappingResponse {
  id: string
  control_id: string
  control_name: string
  compliance_framework_item_id: string
  reference_code: string
  title: string
  coverage_level: string
  coverage_notes?: string | null
  mapped_at: string
  mapped_by: string
  mapped_by_name: string
}

// --- Request types ---

export interface ImportFromLibraryRequest {
  library_id: string
  name: string
  category_id: string
}

export interface CreateMappingRequest {
  control_id: string
  compliance_framework_item_id: string
  coverage_level: 'full' | 'partial' | 'planned' | 'not_assessed'
  coverage_notes?: string
}

export interface UpdateMappingRequest {
  coverage_level: 'full' | 'partial' | 'planned' | 'not_assessed'
  coverage_notes?: string
}

export interface UpdateInterpretationRequest {
  tenant_interpretation: string | null
}

// --- Coverage level helpers ---

export const coverageLevels = ['full', 'partial', 'planned', 'not_assessed'] as const

export const coverageLevelLabels: Record<string, string> = {
  full: 'Full',
  partial: 'Partial',
  planned: 'Planned',
  not_assessed: 'Not Assessed',
}

export const coverageLevelColors: Record<string, string> = {
  full: 'bg-green-100 text-green-700',
  partial: 'bg-yellow-100 text-yellow-700',
  planned: 'bg-blue-100 text-blue-700',
  not_assessed: 'bg-gray-100 text-gray-700',
}

// --- List filters ---

export interface FrameworkListFilter {
  search?: string
  category_id?: string
}

// --- API functions ---

function buildFilterParams(filter?: FrameworkListFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.search) params.set('search', filter.search)
  if (filter.category_id) params.set('category_id', filter.category_id)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function obligationsApi(api: ApiClient) {
  return {
    // Framework library (platform-level, no permission check)
    listLibrary: () => api.get<LibraryEntry[]>('/frameworks/library'),
    getLibrary: (libraryId: string) =>
      api.get<LibraryEntryDetail>(`/frameworks/library/${libraryId}`),

    // Frameworks (permission-filtered by framework category)
    list: (filter?: FrameworkListFilter) =>
      api.get<ComplianceFramework[]>(`/frameworks${buildFilterParams(filter)}`),
    get: (frameworkId: string) =>
      api.get<ComplianceFrameworkDetail>(`/frameworks/${frameworkId}`),
    importFromLibrary: (data: ImportFromLibraryRequest) =>
      api.post<ImportResult>('/frameworks/import', data),
    importFromCSV: async (file: File, name: string, categoryId: string): Promise<ImportResult> => {
      const form = new FormData()
      form.append('file', file)
      form.append('name', name)
      form.append('category_id', categoryId)
      const response = await api.fetch('/frameworks/import', { method: 'POST', body: form })
      return response.json() as Promise<ImportResult>
    },
    delete: (frameworkId: string) => api.del(`/frameworks/${frameworkId}`),

    // Framework items
    listItems: (frameworkId: string) =>
      api.get<FrameworkItem[]>(`/frameworks/${frameworkId}/items`),
    listUnmappedItems: (frameworkId: string) =>
      api.get<FrameworkItem[]>(`/frameworks/${frameworkId}/items/unmapped`),
    getItem: (frameworkId: string, itemId: string) =>
      api.get<FrameworkItemDetail>(`/frameworks/${frameworkId}/items/${itemId}`),
    updateInterpretation: (frameworkId: string, itemId: string, data: UpdateInterpretationRequest) =>
      api.put<{ status: string }>(`/frameworks/${frameworkId}/items/${itemId}`, data),

    // Control-compliance mappings
    createMapping: (frameworkId: string, data: CreateMappingRequest) =>
      api.post<MappingResponse>(`/frameworks/${frameworkId}/mappings`, data),
    updateMapping: (frameworkId: string, mappingId: string, data: UpdateMappingRequest) =>
      api.put<{ status: string }>(`/frameworks/${frameworkId}/mappings/${mappingId}`, data),
    deleteMapping: (frameworkId: string, mappingId: string) =>
      api.del(`/frameworks/${frameworkId}/mappings/${mappingId}`),
  }
}
